import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { api } from '../lib/api';
import { fromLocalInput, toLocalInput } from '../lib/dates';
import { useApi } from '../lib/hooks';
import { humanize, useI18n } from '../lib/i18n';
import { Badge, Button, Checkbox, cx, DataTable, ErrorBox, Field, fieldErrors, Input, Loading, Modal, Pager, PersonPicker, Select, Textarea, useToast, type Column } from './components';

export interface FieldMeta { name: string; type: string; required: boolean; values: string[] | null; ref: string | null; readOnly: boolean; list: boolean; group: string | null; min: number | null; max: number | null }
export interface EntityMeta { name: string; title: string | null; sort: string | null; can: { create: boolean; update: boolean; remove: boolean }; fields: FieldMeta[] }
export type Row = Record<string, any>;

const metaCache = new Map<string, Promise<EntityMeta[]>>();
export function useMeta(slug: string, resource: string): EntityMeta | undefined {
  const { data } = useApi(() => {
    if (!metaCache.has(slug)) metaCache.set(slug, api<EntityMeta[]>(`/v1/t/${slug}/admin/meta`).catch((e) => { metaCache.delete(slug); throw e; }));
    return metaCache.get(slug)!;
  }, [slug]);
  return data?.find((e) => e.name === resource);
}

const optionCache = new Map<string, Promise<{ id: string; label: string }[]>>();
export function useOptions(slug: string, resource: string | null): { id: string; label: string }[] {
  const { data } = useApi(resource ? () => {
    const key = `${slug}:${resource}`;
    if (!optionCache.has(key)) optionCache.set(key, api<{ id: string; label: string }[]>(`/v1/t/${slug}/admin/options/${resource}`).catch(() => []));
    return optionCache.get(key)!;
  } : null, [slug, resource]);
  return data ?? [];
}
export const invalidateOptions = (slug: string, resource: string): void => { optionCache.delete(`${slug}:${resource}`); };

const LIST_FIELDS = ['languages', 'tags', 'channels', 'amenities', 'equipment', 'qualifications', 'images', 'exdates', 'specialisations'];
const ID_LIST_FIELDS: Record<string, string> = { location_ids: 'locations', allowed_location_ids: 'locations', unlimited_token_type_ids: 'token_types' };
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];

function Rows<T extends Row>({ items, onChange, blank, render, max = 20 }: { items: T[]; onChange(v: T[]): void; blank: T; render(item: T, set: (patch: Partial<T>) => void): ReactNode; max?: number }) {
  const i18n = useI18n();
  return (
    <div className="rows">
      {items.map((item, i) => (
        <div className="row gap-sm" key={i}>
          {render(item, (patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x))))}
          <button type="button" className="icon-btn" aria-label={i18n.t('common.remove')} onClick={() => onChange(items.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      {items.length < max && <Button variant="ghost" size="sm" onClick={() => onChange([...items, { ...blank }])}>+ {i18n.t('common.add')}</Button>}
    </div>
  );
}

export function RRuleInput({ value, onChange }: { value: Row | null; onChange(v: Row | null): void }) {
  const i18n = useI18n();
  const v = value ?? null;
  const days: number[] = v?.byweekday ?? [];
  return (
    <div className="stack-sm">
      <div className="row gap-sm wrap">
        <Select value={v?.freq ?? ''} onChange={(e) => onChange(e.target.value ? { freq: e.target.value, interval: v?.interval ?? 1, byweekday: e.target.value === 'weekly' ? days : undefined } : null)}
          options={['daily', 'weekly', 'monthly'].map((f) => ({ value: f, label: i18n.t(`repeat.${f}`) }))} placeholder={i18n.t('repeat.none')} />
        {v?.freq && <label className="row gap-sm">{i18n.t('repeat.every')}<Input type="number" min={1} max={12} style={{ width: 70 }} value={v.interval ?? 1} onChange={(e) => onChange({ ...v, interval: Number(e.target.value) || 1 })} /></label>}
      </div>
      {v?.freq === 'weekly' && (
        <div className="weekday-toggle">
          {WEEKDAYS.map((d) => (
            <button type="button" key={d} className={cx('chip', days.includes(d) && 'active')}
              onClick={() => onChange({ ...v, byweekday: days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort() })}>
              {i18n.day('2024-01-0' + (d === 0 ? 7 : d), { weekday: 'short' })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HoursInput({ value, onChange }: { value: Record<string, string[][]> | null; onChange(v: Record<string, string[][]>): void }) {
  const i18n = useI18n();
  const v = value ?? {};
  return (
    <div className="stack-sm">
      {WEEKDAYS.map((d) => (
        <div className="row gap-sm" key={d}>
          <span style={{ width: 48 }}>{i18n.day('2024-01-0' + (d === 0 ? 7 : d), { weekday: 'short' })}</span>
          <Input placeholder={i18n.t('hours.placeholder')} defaultValue={(v[String(d)] ?? []).map((r) => r.join('-')).join(', ')}
            onBlur={(e) => {
              const ranges = e.target.value.split(',').map((s) => s.trim()).filter(Boolean).map((s) => s.split('-').map((x) => x.trim()))
                .filter((r) => r.length === 2 && /^\d{2}:\d{2}$/.test(r[0]) && /^\d{2}:\d{2}$/.test(r[1]));
              onChange({ ...v, [String(d)]: ranges });
            }} />
        </div>
      ))}
    </div>
  );
}

export function FieldInput({ slug, field, value, onChange }: { slug: string; field: FieldMeta; value: any; onChange(v: any): void }) {
  const i18n = useI18n();
  const refOptions = useOptions(slug, field.type === 'ref' && field.ref !== 'persons' ? field.ref : ID_LIST_FIELDS[field.name] ?? (field.name === 'token_grants' ? 'token_types' : null));
  const [personLabel, setPersonLabel] = useState<string | null>(null);
  useEffect(() => {
    if (field.ref === 'persons' && value && !personLabel) {
      api<{ items: Row[] }>(`/v1/t/${slug}/admin/r/persons`, { query: { id__in: value } })
        .then((r) => { const p = r.items[0]; if (p) setPersonLabel(`${p.first_name} ${p.last_name === '-' ? '' : p.last_name}`.trim()); }).catch(() => undefined);
    }
  }, [value]);
  const str = value === null || value === undefined ? '' : String(value);
  switch (field.type) {
    case 'text': return <Textarea value={str} onChange={(e) => onChange(e.target.value)} />;
    case 'int': return <Input type="number" value={str} min={field.min ?? undefined} max={field.max ?? undefined} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
    case 'money': return <Input type="number" step="0.01" min={0} value={value === null || value === undefined ? '' : (Number(value) / 100).toString()} onChange={(e) => onChange(e.target.value === '' ? null : Math.round(Number(e.target.value) * 100))} />;
    case 'bool': return <Checkbox label={i18n.field(field.name)} checked={Boolean(value)} onChange={onChange} />;
    case 'date': return <Input type="date" value={str.slice(0, 10)} onChange={(e) => onChange(e.target.value || null)} />;
    case 'time': return <Input type="time" value={str.slice(0, 5)} onChange={(e) => onChange(e.target.value || null)} />;
    case 'dt': return <Input type="datetime-local" value={toLocalInput(value, i18n.tz)} onChange={(e) => onChange(fromLocalInput(e.target.value, i18n.tz))} />;
    case 'color': return <Input type="color" value={str || '#4f46e5'} onChange={(e) => onChange(e.target.value)} />;
    case 'enum': return <Select value={str} onChange={(e) => onChange(e.target.value || null)} placeholder={field.required ? undefined : '—'} options={(field.values ?? []).map((x) => ({ value: x, label: i18n.option(x) }))} />;
    case 'enc': return <Input autoComplete="off" placeholder={value ? String(value) : '••••'} onChange={(e) => onChange(e.target.value)} />;
    case 'email': return <Input type="email" value={str} onChange={(e) => onChange(e.target.value)} />;
    case 'phone': return <Input type="tel" value={str} onChange={(e) => onChange(e.target.value)} />;
    case 'url': return <Input type="url" value={str} onChange={(e) => onChange(e.target.value)} />;
    case 'ref':
      if (field.ref === 'persons') {
        return <PersonPicker slug={slug} value={value ? { id: value, label: personLabel ?? '…' } : null} onChange={(p) => { setPersonLabel(p?.label ?? null); onChange(p?.id ?? null); }} />;
      }
      return <Select value={str} onChange={(e) => onChange(e.target.value || null)} placeholder="—" options={refOptions.map((o) => ({ value: o.id, label: o.label }))} />;
    case 'json': {
      if (field.name === 'rrule') return <RRuleInput value={value} onChange={onChange} />;
      if (field.name === 'opening_hours' || field.name === 'staffed_hours' || field.name === 'hours') return <HoursInput value={value} onChange={onChange} />;
      if (ID_LIST_FIELDS[field.name]) {
        const ids: string[] = Array.isArray(value) ? value : [];
        return (
          <div className="stack-sm">
            {refOptions.map((o) => <Checkbox key={o.id} label={o.label} checked={ids.includes(o.id)} onChange={(c) => onChange(c ? [...ids, o.id] : ids.filter((x) => x !== o.id))} />)}
            <small className="muted">{i18n.t('form.empty_means_all')}</small>
          </div>
        );
      }
      if (field.name === 'token_grants') {
        return <Rows items={Array.isArray(value) ? value : []} onChange={onChange} blank={{ token_type_id: refOptions[0]?.id ?? '', quantity: 1 }} render={(item, set) => (<>
          <Select value={item.token_type_id} onChange={(e) => set({ token_type_id: e.target.value })} options={refOptions.map((o) => ({ value: o.id, label: o.label }))} />
          <Input type="number" min={1} style={{ width: 80 }} value={item.quantity} onChange={(e) => set({ quantity: Number(e.target.value) || 1 })} />
        </>)} />;
      }
      if (field.name === 'emergency_contacts') {
        return <Rows max={3} items={Array.isArray(value) ? value : []} onChange={onChange} blank={{ name: '', relation: '', phone: '' }} render={(item, set) => (<>
          <Input placeholder={i18n.field('name')} value={item.name} onChange={(e) => set({ name: e.target.value })} />
          <Input placeholder={i18n.t('health.relation')} value={item.relation} onChange={(e) => set({ relation: e.target.value })} />
          <Input type="tel" placeholder={i18n.field('phone')} value={item.phone} onChange={(e) => set({ phone: e.target.value })} />
        </>)} />;
      }
      if (field.name === 'variants') {
        return <Rows items={Array.isArray(value) ? value : []} onChange={onChange} blank={{ sku: '', name: '' }} render={(item, set) => (<>
          <Input placeholder="SKU" value={item.sku} onChange={(e) => set({ sku: e.target.value })} />
          <Input placeholder={i18n.field('name')} value={item.name} onChange={(e) => set({ name: e.target.value })} />
          <Input type="number" step="0.01" placeholder={i18n.field('price_cents')} value={item.price_cents ? item.price_cents / 100 : ''} onChange={(e) => set({ price_cents: e.target.value ? Math.round(Number(e.target.value) * 100) : undefined })} />
        </>)} />;
      }
      if (LIST_FIELDS.includes(field.name)) {
        return <Input defaultValue={Array.isArray(value) ? value.join(', ') : ''} placeholder={i18n.t('form.comma_separated')}
          onBlur={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />;
      }
      return <JsonInput value={value} onChange={onChange} />;
    }
    default: return <Input value={str} onChange={(e) => onChange(e.target.value)} />;
  }
}

function JsonInput({ value, onChange }: { value: any; onChange(v: any): void }) {
  const i18n = useI18n();
  const [text, setText] = useState(() => (value === null || value === undefined ? '' : JSON.stringify(value, null, 2)));
  const [bad, setBad] = useState(false);
  return (
    <>
      <Textarea className={cx('input mono', bad && 'invalid')} rows={5} value={text} onChange={(e) => setText(e.target.value)}
        onBlur={() => { try { onChange(text.trim() ? JSON.parse(text) : null); setBad(false); } catch { setBad(true); } }} />
      {bad && <small className="field-error">{i18n.t('form.invalid_json')}</small>}
    </>
  );
}

export function formatValue(i18n: ReturnType<typeof useI18n>, field: FieldMeta | undefined, value: any, labels?: Record<string, string>): ReactNode {
  if (value === null || value === undefined || value === '') return <span className="muted">—</span>;
  if (!field) return String(value);
  switch (field.type) {
    case 'money': return i18n.money(Number(value));
    case 'bool': return value ? '✓' : '—';
    case 'dt': return i18n.dateTime(value);
    case 'date': return i18n.date(`${value}T12:00:00Z`, { dateStyle: 'medium', timeZone: 'UTC' });
    case 'enum': return i18n.option(String(value));
    case 'ref': return labels?.[value] ?? <span className="muted">…</span>;
    case 'color': return <span className="swatch" style={{ background: value }} />;
    case 'json': return Array.isArray(value) ? `${value.length}×` : '{…}';
    default: return String(value).length > 80 ? `${String(value).slice(0, 80)}…` : String(value);
  }
}

/** Resolves labels for reference columns on the current page (one request per referenced resource). */
export function useRefLabels(slug: string, meta: EntityMeta | undefined, rows: Row[], fields: FieldMeta[]): Record<string, string> {
  const [labels, setLabels] = useState<Record<string, string>>({});
  const key = rows.map((r) => r.id).join(',');
  useEffect(() => {
    if (!meta) return;
    const byRes = new Map<string, Set<string>>();
    for (const f of fields.filter((x) => x.type === 'ref' && x.ref)) {
      for (const r of rows) if (r[f.name]) { if (!byRes.has(f.ref!)) byRes.set(f.ref!, new Set()); byRes.get(f.ref!)!.add(r[f.name]); }
    }
    for (const [res, ids] of byRes) {
      api<{ items: Row[] }>(`/v1/t/${slug}/admin/r/${res}`, { query: { id__in: [...ids].join(','), pageSize: 500 } }).then((out) => {
        const add: Record<string, string> = {};
        for (const it of out.items) add[it.id] = res === 'persons' ? `${it.first_name} ${it.last_name === '-' ? '' : it.last_name}`.trim() : String(it.name ?? it.title ?? it.number ?? it.code ?? it.id);
        setLabels((l) => ({ ...l, ...add }));
      }).catch(() => undefined);
    }
  }, [meta, key]);
  return labels;
}

export function SchemaForm({ slug, meta, row, defaults, hide = [], onSaved, onCancel, extra }: {
  slug: string; meta: EntityMeta; row?: Row | null; defaults?: Row; hide?: string[]; onSaved(row: Row): void; onCancel?(): void; extra?: ReactNode;
}) {
  const i18n = useI18n();
  const toast = useToast();
  const editing = Boolean(row?.id);
  const [values, setValues] = useState<Row>(() => (editing ? {} : { ...defaults }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const current = (name: string): any => (name in values ? values[name] : row?.[name]);
  const editable = meta.fields.filter((f) => !f.readOnly && !hide.includes(f.name));
  const groups = useMemo(() => {
    const out = new Map<string, FieldMeta[]>();
    for (const f of editable) { const g = f.group ?? 'general'; if (!out.has(g)) out.set(g, []); out.get(g)!.push(f); }
    return [...out.entries()];
  }, [meta, hide.join()]);
  const readOnly = editing ? meta.fields.filter((f) => f.readOnly && row?.[f.name] !== null && row?.[f.name] !== undefined && !hide.includes(f.name)) : [];
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const body = editing ? values : { ...defaults, ...values };
      const saved = editing
        ? await api<Row>(`/v1/t/${slug}/admin/r/${meta.name}/${row!.id}`, { method: 'PATCH', body })
        : await api<Row>(`/v1/t/${slug}/admin/r/${meta.name}`, { body });
      invalidateOptions(slug, meta.name);
      toast.ok(i18n.t('common.saved'));
      onSaved(saved);
    } catch (err) {
      setErrors(fieldErrors(err));
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="stack" onSubmit={submit}>
      {groups.map(([g, fields]) => (
        <fieldset key={g} className="fieldset">
          {groups.length > 1 && <legend>{i18n.has(`group.${g}`) ? i18n.t(`group.${g}`) : humanize(g)}</legend>}
          <div className="grid-2">
            {fields.map((f) => (
              <Field key={f.name} label={f.type === 'bool' ? undefined : `${i18n.field(f.name)}${f.required ? ' *' : ''}`} error={errors[f.name]}
                className={['text', 'json'].includes(f.type) ? 'span-2' : undefined}>
                <FieldInput slug={slug} field={f} value={current(f.name)} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} />
              </Field>
            ))}
          </div>
        </fieldset>
      ))}
      {extra}
      {readOnly.length > 0 && (
        <details className="details"><summary>{i18n.t('form.system_fields')}</summary>
          <dl className="dl">{readOnly.map((f) => <div key={f.name}><dt>{i18n.field(f.name)}</dt><dd>{formatValue(i18n, f, row?.[f.name])}</dd></div>)}</dl>
        </details>
      )}
      <div className="row gap-sm end">
        {onCancel && <Button variant="ghost" onClick={onCancel}>{i18n.t('common.cancel')}</Button>}
        <Button type="submit" busy={busy}>{i18n.t(editing ? 'common.save' : 'common.create')}</Button>
      </div>
    </form>
  );
}

export function ResourcePage({ slug, resource, query = {}, title, columns: columnNames, hide, defaults, onRowClick, actions, rowActions, searchable = true, pageSize = 25 }: {
  slug: string; resource: string; query?: Record<string, string>; title?: ReactNode; columns?: string[]; hide?: string[]; defaults?: Row;
  onRowClick?(row: Row): void; actions?: ReactNode; rowActions?(row: Row, reload: () => void): ReactNode; searchable?: boolean; pageSize?: number;
}) {
  const i18n = useI18n();
  const toast = useToast();
  const meta = useMeta(slug, resource);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Row | null | undefined>(undefined);
  const list = useApi(() => api<{ items: Row[]; total: number; page: number; pageSize: number }>(`/v1/t/${slug}/admin/r/${resource}`, { query: { ...query, q, page, pageSize } }), [slug, resource, JSON.stringify(query), q, page]);
  const fields = useMemo(() => {
    if (!meta) return [];
    const byName = new Map(meta.fields.map((f) => [f.name, f]));
    const names = columnNames ?? meta.fields.filter((f) => f.list && !(hide ?? []).includes(f.name) && !(f.name in query)).map((f) => f.name).slice(0, 6);
    return names.map((n) => byName.get(n)).filter(Boolean) as FieldMeta[];
  }, [meta, columnNames?.join()]);
  const labels = useRefLabels(slug, meta, list.data?.items ?? [], fields);
  if (!meta) return <Loading />;
  const openEdit = async (row: Row): Promise<void> => {
    try { setEditing(await api<Row>(`/v1/t/${slug}/admin/r/${resource}/${row.id}`)); } catch (e) { toast.error(e); }
  };
  const cols: Column<Row>[] = fields.map((f) => ({ key: f.name, label: i18n.field(f.name), render: (r) => (f.name === 'status' || f.name === 'stage' ? <Badge value={r[f.name]} /> : formatValue(i18n, f, r[f.name], labels)) }));
  if (rowActions || meta.can.remove) {
    cols.push({ key: '_actions', label: '', className: 'actions', render: (r) => (
      <div className="row gap-sm end" onClick={(e) => e.stopPropagation()}>
        {rowActions?.(r, list.reload)}
        {meta.can.remove && <Button variant="ghost" size="sm" onClick={async () => {
          if (!window.confirm(i18n.t('common.confirm_delete'))) return;
          try { await api(`/v1/t/${slug}/admin/r/${resource}/${r.id}`, { method: 'DELETE' }); invalidateOptions(slug, resource); list.reload(); } catch (e) { toast.error(e); }
        }}>{i18n.t('common.delete')}</Button>}
      </div>
    ) });
  }
  return (
    <div className="stack">
      <div className="toolbar">
        {title && <h2>{title}</h2>}
        {searchable && <Input type="search" className="input search" placeholder={i18n.t('common.search')} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />}
        <div className="row gap-sm">
          {actions}
          {meta.can.create && <Button onClick={() => setEditing(null)}>+ {i18n.t('common.new')}</Button>}
        </div>
      </div>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : !list.data ? <Loading /> : (
        <>
          <DataTable rows={list.data.items} columns={cols} onRowClick={onRowClick ?? (meta.can.update ? openEdit : undefined)} />
          <Pager page={list.data.page} pageSize={list.data.pageSize} total={list.data.total} onPage={setPage} />
        </>
      )}
      {editing !== undefined && (
        <Modal wide title={`${i18n.entity(resource)}${editing ? '' : ` · ${i18n.t('common.new')}`}`} onClose={() => setEditing(undefined)}>
          <SchemaForm slug={slug} meta={meta} row={editing} defaults={{ ...query, ...defaults }} hide={[...(hide ?? []), ...Object.keys(query)]}
            onSaved={() => { setEditing(undefined); list.reload(); }} onCancel={() => setEditing(undefined)} />
        </Modal>
      )}
    </div>
  );
}

